<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SupportTicketsAPI from 'dashboard/api/supportTickets';
import { frontendURL } from 'dashboard/helper/URLHelper';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const subject = ref('');
const category = ref('technical');
const priority = ref('normal');
const description = ref('');
const files = ref([]);
const isSubmitting = ref(false);
const activeTab = ref('new');
const tickets = ref([]);
const isListLoading = ref(false);

const categoryOptions = computed(() => [
  { value: 'technical', label: t('SUPPORT.NEW.CATEGORY.OPTIONS.TECHNICAL') },
  { value: 'billing', label: t('SUPPORT.NEW.CATEGORY.OPTIONS.BILLING') },
  {
    value: 'integration',
    label: t('SUPPORT.NEW.CATEGORY.OPTIONS.INTEGRATION'),
  },
  {
    value: 'feature_request',
    label: t('SUPPORT.NEW.CATEGORY.OPTIONS.FEATURE_REQUEST'),
  },
  { value: 'other', label: t('SUPPORT.NEW.CATEGORY.OPTIONS.OTHER') },
]);

const priorityOptions = computed(() => [
  { value: 'normal', label: t('SUPPORT.NEW.PRIORITY.OPTIONS.NORMAL') },
  { value: 'high', label: t('SUPPORT.NEW.PRIORITY.OPTIONS.HIGH') },
  { value: 'urgent', label: t('SUPPORT.NEW.PRIORITY.OPTIONS.URGENT') },
]);

const isFormInvalid = computed(
  () =>
    isSubmitting.value ||
    !subject.value.trim() ||
    !description.value.trim()
);

const formatTimestamp = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const buildShowUrl = ticketId =>
  frontendURL(`accounts/${route.params.accountId}/support/tickets/${ticketId}`);

const loadTickets = async () => {
  isListLoading.value = true;
  try {
    const { data } = await SupportTicketsAPI.list();
    tickets.value = data?.tickets || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    useAlert(t('SUPPORT.LIST.LOAD_ERROR'));
  } finally {
    isListLoading.value = false;
  }
};

watch(activeTab, tab => {
  if (tab === 'list' && !tickets.value.length) {
    loadTickets();
  }
});

const ensureShowNavigation = async ticketId => {
  const target = {
    name: 'support_ticket_show',
    params: { accountId: route.params.accountId, ticketId },
  };
  try {
    await router.replace(target);
  } catch (_error) {
    // fallback below will handle navigation
  }

  await nextTick();
  const current = router.currentRoute.value;
  const currentId = current?.params?.ticketId;
  if (current?.name !== 'support_ticket_show' || `${currentId}` !== `${ticketId}`) {
    window.location.assign(buildShowUrl(ticketId));
  }
};

const onFilesChange = event => {
  files.value = Array.from(event.target.files || []);
};

const submitTicket = async () => {
  if (isFormInvalid.value) return;
  isSubmitting.value = true;
  try {
    const payload = new FormData();
    payload.append('subject', subject.value.trim());
    payload.append('category', category.value);
    payload.append('priority', priority.value);
    payload.append('description', description.value.trim());
    files.value.forEach(file => payload.append('attachments[]', file));

    const { data } = await SupportTicketsAPI.create(payload);
    const ticketId = data?.ticket_id || data?.id;
    if (!ticketId) {
      useAlert(t('SUPPORT.NEW.ERROR'));
      return;
    }
    useAlert(t('SUPPORT.NEW.SUCCESS_WITH_ID', { id: ticketId }));
    await ensureShowNavigation(ticketId);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    useAlert(error?.response?.data?.error || t('SUPPORT.NEW.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 p-6 max-w-3xl">
    <div class="flex flex-col gap-2">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('SUPPORT.NEW.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11">
        {{ t('SUPPORT.NEW.DESCRIPTION') }}
      </p>
    </div>

    <div class="inline-flex rounded-lg bg-n-solid-2 p-1">
      <button
        type="button"
        class="px-3 py-1.5 text-sm font-medium rounded-md"
        :class="activeTab === 'new' ? 'bg-n-solid-1 text-n-slate-12' : 'text-n-slate-11'"
        @click="activeTab = 'new'"
      >
        {{ t('SUPPORT.NAV.NEW_TICKET') }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-sm font-medium rounded-md"
        :class="activeTab === 'list' ? 'bg-n-solid-1 text-n-slate-12' : 'text-n-slate-11'"
        @click="activeTab = 'list'"
      >
        {{ t('SUPPORT.NAV.MY_TICKETS') }}
      </button>
    </div>

    <form v-if="activeTab === 'new'" class="grid gap-4" @submit.prevent="submitTicket">
      <WithLabel name="subject" :label="t('SUPPORT.NEW.SUBJECT.LABEL')">
        <NextInput
          v-model="subject"
          type="text"
          class="w-full"
          :placeholder="t('SUPPORT.NEW.SUBJECT.PLACEHOLDER')"
          :disabled="isSubmitting"
        />
      </WithLabel>

      <WithLabel name="category" :label="t('SUPPORT.NEW.CATEGORY.LABEL')">
        <select
          v-model="category"
          class="!mb-0 text-sm"
          :disabled="isSubmitting"
        >
          <option
            v-for="option in categoryOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </WithLabel>

      <WithLabel name="priority" :label="t('SUPPORT.NEW.PRIORITY.LABEL')">
        <select
          v-model="priority"
          class="!mb-0 text-sm"
          :disabled="isSubmitting"
        >
          <option
            v-for="option in priorityOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </WithLabel>

      <WithLabel name="description" :label="t('SUPPORT.NEW.MESSAGE.LABEL')">
        <textarea
          v-model="description"
          rows="6"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm text-n-slate-12"
          :placeholder="t('SUPPORT.NEW.MESSAGE.PLACEHOLDER')"
          :disabled="isSubmitting"
        />
      </WithLabel>

      <WithLabel name="attachments" :label="t('SUPPORT.NEW.ATTACHMENTS.LABEL')">
        <input
          type="file"
          multiple
          class="text-sm"
          :disabled="isSubmitting"
          @change="onFilesChange"
        />
      </WithLabel>

      <div>
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :disabled="isFormInvalid"
        >
          {{ t('SUPPORT.NEW.SUBMIT') }}
        </NextButton>
      </div>
    </form>

    <div v-else class="flex flex-col gap-4">
      <div v-if="isListLoading" class="text-sm text-n-slate-11">
        {{ t('SUPPORT.LIST.LOADING') }}
      </div>
      <div v-else-if="!tickets.length" class="text-sm text-n-slate-11">
        {{ t('SUPPORT.LIST.EMPTY') }}
      </div>
      <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
        <table class="min-w-full text-sm">
          <thead class="bg-n-solid-2 text-n-slate-11">
            <tr class="text-left">
              <th class="py-2 px-3">{{ t('SUPPORT.LIST.SUBJECT') }}</th>
              <th class="py-2 px-3">{{ t('SUPPORT.LIST.STATUS') }}</th>
              <th class="py-2 px-3">{{ t('SUPPORT.LIST.PRIORITY') }}</th>
              <th class="py-2 px-3">{{ t('SUPPORT.LIST.LAST_ACTIVITY') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="ticket in tickets"
              :key="ticket.id"
              class="border-t border-n-weak hover:bg-n-solid-3"
            >
              <td class="py-2 px-3">
                <button
                  type="button"
                  class="text-woot-500 hover:text-woot-700"
                  @click="
                    router.push({
                      name: 'support_ticket_show',
                      params: { accountId: route.params.accountId, ticketId: ticket.id },
                    })
                  "
                >
                  {{ ticket.subject }}
                </button>
                <span
                  v-if="ticket.unread"
                  class="ml-2 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                >
                  {{ t('SUPPORT.LIST.UNREAD') }}
                </span>
              </td>
              <td class="py-2 px-3">{{ ticket.status }}</td>
              <td class="py-2 px-3">{{ ticket.priority }}</td>
              <td class="py-2 px-3">{{ formatTimestamp(ticket.last_activity_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
